import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { formaterNomFamille, formaterPrenom, formaterTelephone } from '../components/Shared';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';


// Choix explicite « aucune des dates proposées ne me convient » (valeur de session_id du stagiaire, gérée côté serveur)
const SESSION_AUCUNE = 'AUCUNE';

const STAGIAIRE_VIDE = {
  civilite: '', nom_usage: '', nom_naissance: '', prenom: '',
  date_naissance: '', nss: '', tel: '',
  poste: '', handicap: '', handicap_detail: '', difficultes: '', difficultes_oui: '',
};

const inputCls = {
  width: '100%', padding: '0.45rem 0.65rem', borderRadius: '6px',
  border: '1px solid #d1d5db', background: '#fff', fontSize: '0.875rem',
  color: '#111', boxSizing: 'border-box',
};
const selectCls = { ...inputCls, cursor: 'pointer' };
const labelCls = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.25rem' };

function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={labelCls}>{label}{required && <span style={{ color: '#ef4444' }}> *</span>}</label>
      {children}
    </div>
  );
}

function Section({ title, children, color = '#E87722' }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ background: color, color: '#fff', padding: '0.4rem 1rem', borderRadius: '6px 6px 0 0', fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.02em' }}>
        {title}
      </div>
      <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '1rem', background: '#fff' }}>
        {children}
      </div>
    </div>
  );
}

function CiviliteSelect({ value, onChange, style }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{ ...selectCls, ...style }}>
      <option value="">—</option>
      <option value="Mme">Mme</option>
      <option value="M.">M.</option>
    </select>
  );
}

function Checkbox({ checked, onChange, label }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.88rem', marginRight: '1rem' }}>
      <div onClick={() => onChange(!checked)} style={{
        width: 18, height: 18, borderRadius: 4, border: '2px solid #E87722',
        background: checked ? '#E87722' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0,
      }}>
        {checked && <span style={{ color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>✓</span>}
      </div>
      {label}
    </label>
  );
}

export default function FicheInscriptionClient() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Mode "directe" = lien sans devis, via /fiche-inscription-directe/:token
  const isDirecte = location.pathname.startsWith('/fiche-inscription-directe/');
  const apiGet  = isDirecte
    ? `${API_BASE}/inscription-public/${token}`
    : `${API_BASE}/devis-public/${token}/fiche-inscription`;
  const apiPost = isDirecte
    ? `${API_BASE}/inscription-public/${token}/soumettre`
    : `${API_BASE}/devis-public/${token}/fiche-inscription`;

  const [loading, setLoading] = useState(true);
  const [devis, setDevis] = useState(null);
  const [ficheExistante, setFicheExistante] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [phoneModal, setPhoneModal] = useState(null); // null | 'completer' | 'verifier' (info affichée après l'enregistrement)

  // Mode admin : utilisateur de l'application connecté (bypass du blocage client)
  const adminToken = (() => {
    try {
      const t = localStorage.getItem('gf_token');
      if (!t) return null;
      const payload = JSON.parse(atob(t.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) return null;
      if (!['admin', 'utilisateur'].includes(payload.role)) return null;
      return t;
    } catch { return null; }
  })();
  const isAdminMode = !!adminToken;

  // Formulaire
  const [typeFormation, setTypeFormation] = useState('initiale');
  const [inscriptionType, setInscriptionType] = useState('entreprise'); // 'entreprise' | 'particulier'

  // Entreprise
  const [ent, setEnt] = useState({ raison_sociale: '', siret: '', adresse: '', cp: '', ville: '', tel: '' });
  const [rep, setRep] = useState({ civilite: '', nom: '', prenom: '' });
  const [contact, setContact] = useState({ civilite: '', nom: '', prenom: '', tel: '', mail: '' });

  // Particulier
  const [part, setPart] = useState({ civilite: '', nom: '', prenom: '', tel: '', mail: '' });

  // Facturation
  const [fact, setFact] = useState({ destinataire: '', adresse: '', cp: '', ville: '' });

  // Activités
  const [activitesList, setActivitesList] = useState([]);
  const [activitesChoisies, setActivitesChoisies] = useState([]);
  const [activiteAutre, setActiviteAutre] = useState('');
  const [autreActivite, setAutreActivite] = useState('');

  // Stagiaires
  const [stagiaires, setStagiaires] = useState([{ ...STAGIAIRE_VIDE }]);

  // Choix des sessions
  const [sessionsChoix, setSessionsChoix] = useState([]);
  const [sessionPourTous, setSessionPourTous] = useState('');
  const [sessionsParStagiaire, setSessionsParStagiaire] = useState({});
  const [datesSouhaitees, setDatesSouhaitees] = useState(''); // précisions du client quand aucune date ne convient
  const [contactCej, setContactCej] = useState({ email: '', tel: '' });

  // Signature
  const [faitA, setFaitA] = useState('');
  const [faitLe, setFaitLe] = useState(new Date().toISOString().slice(0, 10));

  // Stagiaires existants (depuis la BDD CEJ)
  const [stagiairesExistants, setStagiairesExistants] = useState([]);
  // Blocage session
  const [blocage, setBlocage] = useState(null); // { isBlocked, sessionDate, jours, email }
  const [showBlocagePopup, setShowBlocagePopup] = useState(false);

  useEffect(() => {
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    fetch(apiGet, { headers })
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); setLoading(false); return; }
        setDevis(data.devis);
        setActivitesList(data.activites_list || []);
        // Sessions disponibles
        const sessions = data.sessions_choix || [];
        setSessionsChoix(sessions);
        setContactCej({ email: data.societe_email || '', tel: data.societe_tel || '' });

        if (data.fiche) {
          prefillFiche(data.fiche, sessions);
          setFicheExistante(data.fiche);
          if (data.fiche.statut === 'soumise') setSubmitted(true);
        } else if (data.client || data.devis) {
          prefillFromClient(data.client, data.devis);
        }

        // Calcul du blocage session (désactivé en mode admin)
        const sessionDate = data.devis?.session?.start_date;
        const blocageJours = parseInt(data.blocage_jours || '7');
        if (sessionDate && !adminToken) {
          const diffDays = (new Date(sessionDate) - new Date()) / 86400000;
          const depasseDelai = diffDays <= blocageJours;
          // Exceptions : premier envoi (fiche jamais soumise) ou renvoi explicite par l'utilisateur
          const premierEnvoi   = !data.fiche_deja_soumise;
          const debloque       = data.modification_debloquee === true;
          const isBlocked      = depasseDelai && !premierEnvoi && !debloque;
          setBlocage({
            isBlocked,
            depasseDelai,   // délai dépassé même si pas bloqué (pour affichage info)
            premierEnvoi,
            debloque,
            sessionDate,
            jours: blocageJours,
            email: data.societe_email || 'formation@cejconcept.fr',
          });
          if (isBlocked) setShowBlocagePopup(true);
        }

        setLoading(false);

        // Chargement des stagiaires existants pour ce client
        const stagUrl = isDirecte
          ? `${API_BASE}/inscription-public/${token}/stagiaires`
          : `${API_BASE}/devis-public/${token}/stagiaires`;
        fetch(stagUrl, { headers })
          .then(r2 => r2.json())
          .then(d => setStagiairesExistants(d.stagiaires || []))
          .catch(() => {});
      })
      .catch(() => { setError('Erreur de connexion'); setLoading(false); });
  }, [token]);

  function prefillFiche(f, sessions) {
    setTypeFormation(f.type_formation || 'initiale');
    setInscriptionType(f.entreprise_raison_sociale ? 'entreprise' : 'particulier');
    setEnt({ raison_sociale: f.entreprise_raison_sociale || '', siret: f.entreprise_siret || '', adresse: f.entreprise_adresse || '', cp: f.entreprise_cp || '', ville: f.entreprise_ville || '', tel: formaterTelephone(f.entreprise_tel) });
    setRep({ civilite: f.representant_civilite || '', nom: formaterNomFamille(f.representant_nom), prenom: formaterPrenom(f.representant_prenom) });
    setContact({ civilite: f.contact_civilite || '', nom: formaterNomFamille(f.contact_nom), prenom: formaterPrenom(f.contact_prenom), tel: formaterTelephone(f.contact_tel), mail: f.contact_mail || '' });
    setPart({ civilite: f.particulier_civilite || '', nom: formaterNomFamille(f.particulier_nom), prenom: formaterPrenom(f.particulier_prenom), tel: formaterTelephone(f.particulier_tel), mail: f.particulier_mail || '' });
    setFact({ destinataire: f.facturation_destinataire || '', adresse: f.facturation_adresse || '', cp: f.facturation_cp || '', ville: f.facturation_ville || '' });
    setActivitesChoisies(Array.isArray(f.activites) ? f.activites : (f.activites ? JSON.parse(f.activites) : []));
    setActiviteAutre(f.activite_autre || '');
    setAutreActivite(f.autre_activite || '');
    const stagsBruts = Array.isArray(f.stagiaires) ? (f.stagiaires.length ? f.stagiaires : [{ ...STAGIAIRE_VIDE }]) : [{ ...STAGIAIRE_VIDE }];
    const stagsArr = stagsBruts.map(s => ({
      ...s,
      nom_usage: formaterNomFamille(s.nom_usage), nom_naissance: formaterNomFamille(s.nom_naissance),
      prenom: formaterPrenom(s.prenom), tel: formaterTelephone(s.tel),
    }));
    setStagiaires(stagsArr);
    setFaitA(f.fait_a || '');
    setFaitLe(f.fait_le ? f.fait_le.slice(0, 10) : new Date().toISOString().slice(0, 10));

    // Restaurer les choix de session depuis les stagiaires enregistrés
    const remplis = stagsArr.filter(s => s.nom_usage || s.nom_naissance || s.prenom);
    if (remplis.length > 0 && remplis[0].session_id) {
      const premId = remplis[0].session_id;
      const tousSame = remplis.every(s => s.session_id === premId);
      if (tousSame) {
        setSessionPourTous(premId);
      } else {
        const parStag = {};
        remplis.forEach((s, idx) => { if (s.session_id) parStag[idx] = s.session_id; });
        setSessionsParStagiaire(parStag);
      }
    }
    setDatesSouhaitees(remplis.find(s => s.session_id === SESSION_AUCUNE && s.dates_souhaitees)?.dates_souhaitees || '');
  }

  // Pré-remplissage depuis les données client (première ouverture, fiche vierge)
  function prefillFromClient(client, devis) {
    const estParticulier = client?.type_client === 'Particulier';
    setInscriptionType(estParticulier ? 'particulier' : 'entreprise');

    if (estParticulier) {
      // Particulier
      setPart({
        civilite: '',
        nom: formaterNomFamille(client?.nom_client || ''),
        prenom: '',
        tel: formaterTelephone(client?.tel_portable || client?.tel_fixe || ''),
        mail: devis?.email_client || '',
      });
    } else {
      // Entreprise / Organisme
      setEnt({
        raison_sociale: client?.nom_client || devis?.client_name || '',
        siret: client?.siret || '',
        adresse: client?.adresse1 || '',
        cp: client?.code_postal || '',
        ville: client?.ville || '',
        tel: formaterTelephone(client?.tel_fixe || client?.tel_portable || ''),
      });

      // Représentant légal
      if (client?.representant) {
        const mots = client.representant.trim().split(/\s+/);
        setRep({
          civilite: '',
          prenom: formaterPrenom(mots[0] || ''),
          nom: formaterNomFamille(mots.slice(1).join(' ') || ''),
        });
      }

      // Personne chargée de l'inscription (contact)
      setContact({
        civilite: '',
        nom: formaterNomFamille(client?.contact_nom || ''),
        prenom: formaterPrenom(client?.contact_prenom || ''),
        tel: formaterTelephone(client?.contact_tel_portable || client?.contact_tel_fixe || ''),
        mail: client?.contact_email || devis?.email_client || '',
      });

      // Facturation
      setFact({
        destinataire: client?.nom_client || devis?.client_name || '',
        adresse: client?.adresse_facturation1 || client?.adresse1 || '',
        cp: client?.code_postal_facturation || client?.code_postal || '',
        ville: client?.ville_facturation || client?.ville || '',
      });
    }

    // Activités déjà enregistrées pour ce client
    if (client?.activites) {
      const acts = Array.isArray(client.activites) ? client.activites : (typeof client.activites === 'string' ? JSON.parse(client.activites) : []);
      if (acts.length) setActivitesChoisies(acts);
    }

    // Lieu de signature (ville du client si disponible)
    if (client?.ville) setFaitA(client.ville);
  }

  function toggleActivite(a) {
    setActivitesChoisies(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);
  }

  function updateStagiaire(idx, key, val) {
    setStagiaires(prev => prev.map((s, i) => i === idx ? { ...s, [key]: val } : s));
  }

  // Limite selon le chiffrage du devis
  const maxStagiaires = (() => {
    if (!devis) return null;
    if (devis.unite === 'Stagiaire' && devis.qte) return parseInt(devis.qte);
    if (devis.unite === 'Groupe'    && devis.stagiaires_max) return parseInt(devis.stagiaires_max);
    return null;
  })();

  function addStagiaire() {
    if (maxStagiaires !== null && stagiaires.length >= maxStagiaires) return;
    setStagiaires(prev => [...prev, { ...STAGIAIRE_VIDE }]);
  }

  function removeStagiaire(idx) {
    if (stagiaires.length === 1) return;
    setStagiaires(prev => prev.filter((_, i) => i !== idx));
  }

  function buildBody(extraFields) {
    return {
      intitule_formation: devis.intitule_formation,
      type_formation: typeFormation,
      entreprise_raison_sociale: inscriptionType === 'entreprise' ? ent.raison_sociale : '',
      entreprise_siret: inscriptionType === 'entreprise' ? ent.siret : '',
      entreprise_adresse: inscriptionType === 'entreprise' ? ent.adresse : '',
      entreprise_cp: inscriptionType === 'entreprise' ? ent.cp : '',
      entreprise_ville: inscriptionType === 'entreprise' ? ent.ville : '',
      entreprise_tel: inscriptionType === 'entreprise' ? ent.tel : '',
      representant_civilite: rep.civilite,
      representant_nom: rep.nom,
      representant_prenom: rep.prenom,
      contact_civilite: contact.civilite,
      contact_nom: contact.nom,
      contact_prenom: contact.prenom,
      contact_tel: contact.tel,
      contact_mail: contact.mail,
      particulier_civilite: inscriptionType === 'particulier' ? part.civilite : '',
      particulier_nom: inscriptionType === 'particulier' ? part.nom : '',
      particulier_prenom: inscriptionType === 'particulier' ? part.prenom : '',
      particulier_tel: inscriptionType === 'particulier' ? part.tel : '',
      particulier_mail: inscriptionType === 'particulier' ? part.mail : '',
      facturation_destinataire: fact.destinataire,
      facturation_adresse: fact.adresse,
      facturation_cp: fact.cp,
      facturation_ville: fact.ville,
      activites: activitesChoisies,
      activite_autre: activiteAutre,
      autre_activite: autreActivite,
      stagiaires: stagiaires.filter(s => s.nom_usage || s.nom_naissance || s.prenom).map((s, idx) => {
        const sid = sessionPourTous || sessionsParStagiaire[idx] || '';
        const { dates_souhaitees, a_planifier_clos, ...reste } = s; // champs gérés ici / côté serveur
        return { ...reste, session_id: sid, ...(sid === SESSION_AUCUNE ? { dates_souhaitees: datesSouhaitees.trim() } : {}) };
      }),
      fait_a: faitA,
      fait_le: faitLe,
      ...extraFields,
    };
  }

  async function handleSubmit() {
    setSubmitAttempted(true);
    setError('');
    if (inscriptionType === 'entreprise' && !ent.raison_sociale)
      return setError('La raison sociale est requise.');
    if (inscriptionType === 'particulier' && !part.nom)
      return setError('Le nom du particulier est requis.');
    if (!faitA) return setError('Veuillez indiquer le lieu de signature.');

    // Blocage si aucun stagiaire n'est saisi
    const stagiairesRemplis = stagiaires.filter(s => s.nom_naissance || s.nom_usage || s.prenom);
    if (stagiairesRemplis.length === 0)
      return setError('Veuillez saisir au moins un stagiaire avant de soumettre la fiche.');

    // Vérification session obligatoire si des sessions sont disponibles
    if (sessionsChoix.length > 0) {
      const sessionManquante = stagiairesRemplis.some(
        (_, idx) => !(sessionPourTous || sessionsParStagiaire[idx])
      );
      if (sessionManquante) {
        return setError('Veuillez sélectionner une session pour chaque stagiaire avant de soumettre la fiche.');
      }
    }

    // Vérification limite du devis
    if (maxStagiaires !== null && stagiairesRemplis.length > maxStagiaires) {
      return setError(
        `Nombre de stagiaires dépassé : vous ne pouvez inscrire que ${maxStagiaires} stagiaire${maxStagiaires > 1 ? 's' : ''} selon ce devis.`
      );
    }

    // Validation des champs obligatoires des stagiaires
    for (let i = 0; i < stagiaires.length; i++) {
      const s = stagiaires[i];
      if (!s.nom_naissance && !s.nom_usage && !s.prenom) continue;
      if (!s.civilite) return setError(`Stagiaire ${i + 1} : la civilité est obligatoire.`);
      if (!s.nom_naissance) return setError(`Stagiaire ${i + 1} : le nom de naissance est obligatoire.`);
      if (!s.nom_usage) return setError(`Stagiaire ${i + 1} : le nom d'usage est obligatoire.`);
      if (!s.prenom) return setError(`Stagiaire ${i + 1} : le prénom est obligatoire.`);
      if (!s.date_naissance) return setError(`Stagiaire ${i + 1} : la date de naissance est obligatoire.`);
      if (!s.nss) return setError(`Stagiaire ${i + 1} : le numéro de sécurité sociale est obligatoire.`);
      if (!s.handicap) return setError(`Stagiaire ${i + 1} : le champ "Personne en situation de handicap" est obligatoire.`);
      if (s.handicap === 'Oui' && !s.handicap_detail?.trim()) return setError(`Stagiaire ${i + 1} : veuillez préciser la nature du handicap.`);
      if (!s.difficultes) return setError(`Stagiaire ${i + 1} : le champ "Difficultés particulières" est obligatoire.`);
      if (s.difficultes === 'Oui' && !s.difficultes_oui?.trim()) return setError(`Stagiaire ${i + 1} : veuillez préciser les difficultés.`);
    }

    setSaving(true);
    try {
      const postHeaders = { 'Content-Type': 'application/json' };
      if (adminToken) postHeaders['Authorization'] = `Bearer ${adminToken}`;
      const r = await fetch(apiPost, {
        method: 'POST',
        headers: postHeaders,
        body: JSON.stringify(buildBody()),
      });
      const data = await r.json();
      if (!r.ok) {
        if (r.status === 403 && data.blocage) {
          // Blocage côté serveur : mettre à jour l'état et afficher le popup
          setBlocage(prev => ({ ...prev, isBlocked: true, jours: data.jours || prev?.jours || 7, email: data.email || prev?.email || 'formation@cejconcept.fr' }));
          setShowBlocagePopup(true);
          return;
        }
        return setError(data.error || 'Erreur lors de la soumission');
      }
      setSubmitted(true);
      // Le déblocage (premier envoi ou lien renvoyé) est consommé : dans le délai de blocage, la fiche redevient verrouillée
      setBlocage(prev => (prev && prev.depasseDelai ? { ...prev, isBlocked: true, premierEnvoi: false, debloque: false } : prev));
      // Information (non bloquante) sur le portable des stagiaires, pour le SMS de rappel
      if (!adminToken) {
        setPhoneModal(stagiaires.some(s => !(s.tel || '').trim()) ? 'completer' : 'verifier');
      }
    } catch (e) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
      <div style={{ color: '#6b7280', fontSize: '1rem' }}>Chargement...</div>
    </div>
  );

  if (error && !devis) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '2rem 2.5rem', textAlign: 'center', maxWidth: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <p style={{ color: '#ef4444', fontWeight: 600 }}>{error}</p>
      </div>
    </div>
  );

  // Au moins un stagiaire pour lequel aucune des dates proposées ne convient
  const aucuneChoisie = sessionPourTous === SESSION_AUCUNE || stagiaires
    .filter(s => s.nom_usage || s.nom_naissance || s.prenom)
    .some((_, idx) => sessionsParStagiaire[idx] === SESSION_AUCUNE);

  // Information après enregistrement : portable des stagiaires (SMS de rappel de la formation)
  const phoneModalEl = phoneModal && (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div role="dialog" aria-modal="true" style={{ background: '#fff', borderRadius: 16, padding: '2rem 2rem 1.5rem', maxWidth: 460, width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.22)', textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📱</div>
        <p style={{ color: '#374151', fontSize: '0.98rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>
          {phoneModal === 'completer'
            ? 'Merci de compléter le numéro de portable du(des) stagiaire(s) afin de permettre l\'envoi du SMS de rappel de la formation.'
            : 'Merci de vérifier le numéro de portable du stagiaire afin de permettre l\'envoi du SMS de rappel de la formation.'}
        </p>
        <button
          autoFocus
          onClick={() => setPhoneModal(null)}
          style={{ padding: '0.55rem 2rem', borderRadius: 8, border: 'none', background: '#E87722', color: '#fff', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}>
          OK
        </button>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      {phoneModalEl}
      <div style={{ background: '#fff', borderRadius: 16, padding: '2.5rem', maxWidth: 500, width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '2rem' }}>✅</div>
        <h2 style={{ color: '#065f46', marginBottom: '0.75rem', fontSize: '1.3rem' }}>Fiche d'inscription soumise !</h2>
        <p style={{ color: '#6b7280', marginBottom: '1rem', lineHeight: 1.6 }}>
          Merci, votre fiche d'inscription pour la formation<br/>
          <strong style={{ color: '#111' }}>{devis?.intitule_formation}</strong><br/>
          a bien été transmise à CEJ Concept.<br/>
          Vous recevrez une confirmation par email.
        </p>
        {aucuneChoisie && (
          <p style={{ color: '#9a3412', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: '0.6rem 0.8rem', marginBottom: '1rem', fontSize: '0.88rem', lineHeight: 1.5 }}>
            Aucune des dates proposées ne vous convenait : nous vous recontacterons pour vous proposer une nouvelle date.
          </p>
        )}
        {ficheExistante && (
          <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
            Vous pouvez modifier votre fiche en revenant sur cette page.
          </p>
        )}
        {blocage?.isBlocked ? (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ color: '#9a3412', fontSize: '0.88rem', marginBottom: '0.5rem' }}>
              🔒 La session est dans moins de {blocage.jours} jours — les modifications ne sont plus acceptées.
            </p>
            <a
              href={`mailto:${blocage.email}?subject=Demande de modification fiche inscription — ${devis?.intitule_formation || ''}`}
              style={{ display: 'inline-block', padding: '0.4rem 1rem', borderRadius: 6, background: '#ea580c', color: '#fff', fontWeight: 600, fontSize: '0.83rem', textDecoration: 'none' }}>
              ✉️ Contacter CEJ pour modifier
            </a>
          </div>
        ) : (
          <button
            onClick={() => setSubmitted(false)}
            style={{ marginTop: '1rem', padding: '0.5rem 1.2rem', borderRadius: 8, border: '1px solid #E87722', background: '#fff', color: '#E87722', cursor: 'pointer', fontWeight: 600 }}>
            ✏️ Modifier la fiche
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', fontFamily: 'Arial, sans-serif' }}>
      {phoneModalEl}

      {/* Popup blocage session */}
      {showBlocagePopup && blocage?.isBlocked && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            background: '#fff', borderRadius: 16,
            padding: '2rem 2rem 1.5rem',
            maxWidth: 480, width: '100%',
            boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🔒</div>
            <h2 style={{ color: '#c2410c', fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.75rem' }}>
              Modification non autorisée
            </h2>
            <p style={{ color: '#374151', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              La session débutera dans <strong>moins de {blocage.jours} jours</strong>.<br/>
              Les modifications de la fiche d'inscription ne sont plus acceptées via ce formulaire sans l'accord de CEJ Concept.
            </p>
            <p style={{ color: '#6b7280', fontSize: '0.85rem', lineHeight: 1.5, marginBottom: '1.5rem' }}>
              Pour toute modification urgente, veuillez contacter directement notre équipe.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <a
                href={`mailto:${blocage.email}?subject=Demande de modification fiche inscription — ${devis?.intitule_formation || ''}&body=Bonjour,%0D%0A%0D%0AJe souhaite modifier la fiche d'inscription pour la formation : ${devis?.intitule_formation || ''}.%0D%0A%0D%0AModification souhaitée :%0D%0A`}
                style={{ display: 'block', padding: '0.6rem 1.2rem', borderRadius: 8, background: '#ea580c', color: '#fff', fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none' }}>
                ✉️ Demander une modification par mail
              </a>
              <button
                onClick={() => setShowBlocagePopup(false)}
                style={{ padding: '0.55rem 1.2rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
                Consulter la fiche (lecture seule)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ background: '#E87722', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img src="/logo.jpg" alt="CEJ Concept" style={{ height: 44, borderRadius: 4 }} />
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem' }}>Fiche d'inscription</div>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.82rem' }}>{devis?.intitule_formation}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {isAdminMode && (
            <div style={{ background: '#fef3c7', borderRadius: 8, padding: '0.3rem 0.8rem', color: '#92400e', fontSize: '0.78rem', fontWeight: 700, border: '1px solid #f59e0b' }}>
              🔑 Mode admin — blocage désactivé
            </div>
          )}
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 8, padding: '0.3rem 0.8rem', color: '#fff', fontSize: '0.8rem', fontWeight: 600 }}>
            {devis?.devis_id}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>
        {/* Intro */}
        <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.88rem', color: '#92400e' }}>
          <strong>Toutes les rubriques doivent être renseignées.</strong> Ce formulaire peut être sauvegardé et modifié avant envoi définitif.
          {ficheExistante && <span style={{ marginLeft: 8, color: '#059669', fontWeight: 600 }}>📋 Une fiche précédente a été rechargée.</span>}
        </div>
        {isAdminMode && ficheExistante && (
          <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: '0.65rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#5b21b6' }}>
            🔑 <strong>Modification par l'équipe CEJ.</strong> L'exemplaire envoyé par le client est conservé tel quel
            (PDF « avant modification CEJ », visible dans « Versions précédentes de la fiche »). Les modifications enregistrées ici remplacent la fiche de travail.
          </div>
        )}
        {!ficheExistante && (
          <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, padding: '0.65rem 1rem', marginBottom: '1.5rem', fontSize: '0.85rem', color: '#1e40af' }}>
            ✏️ <strong>Certains champs ont été pré-remplis</strong> avec les informations que nous avons pour vous.
            Vérifiez et corrigez si nécessaire avant de soumettre.
          </div>
        )}

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.7rem 1rem', marginBottom: '1rem', color: '#b91c1c', fontSize: '0.88rem' }}>
            ⚠️ {error}
          </div>
        )}

        {/* Bannière blocage session */}
        {blocage?.isBlocked && (
          <div style={{ background: '#fff7ed', border: '1px solid #f97316', borderRadius: 8, padding: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.3rem' }}>🔒</span>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, color: '#c2410c', marginBottom: '0.3rem', fontSize: '0.92rem' }}>
                Modification de la fiche non autorisée
              </p>
              <p style={{ color: '#9a3412', fontSize: '0.85rem', lineHeight: 1.5 }}>
                La session débutera dans moins de {blocage.jours} jours. Les modifications ne sont plus acceptées via ce formulaire.
                Pour toute modification, veuillez contacter CEJ Concept directement.
              </p>
              <a
                href={`mailto:${blocage.email}?subject=Demande de modification fiche inscription — ${devis?.intitule_formation || ''}&body=Bonjour,%0D%0A%0D%0AJe souhaite modifier la fiche d'inscription pour la formation : ${devis?.intitule_formation || ''}.%0D%0A%0D%0AModification souhaitée :%0D%0A`}
                style={{ display: 'inline-block', marginTop: '0.5rem', padding: '0.4rem 1rem', borderRadius: 6, background: '#ea580c', color: '#fff', fontWeight: 600, fontSize: '0.83rem', textDecoration: 'none' }}>
                ✉️ Demander une modification par mail
              </a>
            </div>
          </div>

        )}
        {/* Bannière info : délai dépassé mais accès autorisé (premier envoi ou renvoyé) */}
        {blocage?.depasseDelai && !blocage?.isBlocked && !isAdminMode && (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.85rem' }}>
            <span style={{ fontSize: '1.1rem' }}>
              {blocage.debloque ? '🔓' : '📋'}
            </span>
            <span style={{ color: '#166534' }}>
              {blocage.debloque
                ? <>La fiche vous a été <strong>renvoyée</strong> — vous pouvez la compléter ou la modifier malgré le délai.</>
                : <>C'est votre <strong>premier envoi</strong> — vous pouvez compléter votre fiche librement.</>
              }
            </span>
          </div>
        )}

        {/* Section 1 : Formation */}
        <Section title="FORMATION">
          <Field label="Intitulé de la formation">
            <input type="text" value={devis?.intitule_formation || ''} readOnly style={{ ...inputCls, background: '#f3f4f6', color: '#6b7280' }} />
          </Field>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <Checkbox checked={typeFormation === 'initiale'} onChange={() => setTypeFormation('initiale')} label="Initiale" />
            <Checkbox checked={typeFormation === 'recyclage'} onChange={() => setTypeFormation('recyclage')} label="Recyclage" />
          </div>
          <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>
            Attention, l'inscription à certaines formations entraîne le respect de prérequis (se référer au programme de formation transmis).
          </p>
        </Section>

        {/* Choix entreprise / particulier */}
        <Section title="TYPE D'INSCRIPTION">
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
            <button onClick={() => setInscriptionType('entreprise')} style={{ flex: 1, padding: '0.75rem', borderRadius: 8, border: `2px solid ${inscriptionType === 'entreprise' ? '#E87722' : '#e5e7eb'}`, background: inscriptionType === 'entreprise' ? '#fff7ed' : '#fff', color: inscriptionType === 'entreprise' ? '#E87722' : '#6b7280', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
              🏢 Entreprise / Organisme
            </button>
            <button onClick={() => setInscriptionType('particulier')} style={{ flex: 1, padding: '0.75rem', borderRadius: 8, border: `2px solid ${inscriptionType === 'particulier' ? '#E87722' : '#e5e7eb'}`, background: inscriptionType === 'particulier' ? '#fff7ed' : '#fff', color: inscriptionType === 'particulier' ? '#E87722' : '#6b7280', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
              👤 Particulier
            </button>
          </div>
        </Section>

        {/* Section 2 : Entreprise */}
        {inscriptionType === 'entreprise' && (
          <Section title="ENTREPRISE OU ORGANISME">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
              <Field label="Raison sociale" required><input type="text" value={ent.raison_sociale} onChange={e => setEnt(v => ({ ...v, raison_sociale: e.target.value }))} style={{ ...inputCls, borderColor: submitAttempted && !ent.raison_sociale ? '#ef4444' : inputCls.border }} /></Field>
              <Field label="SIRET"><input type="text" value={ent.siret} onChange={e => setEnt(v => ({ ...v, siret: e.target.value }))} style={inputCls} placeholder="XXX XXX XXX XXXXX" /></Field>
            </div>
            <Field label="Adresse"><input type="text" value={ent.adresse} onChange={e => setEnt(v => ({ ...v, adresse: e.target.value }))} style={inputCls} /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: '0 1rem' }}>
              <Field label="Code postal"><input type="text" value={ent.cp} onChange={e => setEnt(v => ({ ...v, cp: e.target.value }))} style={inputCls} /></Field>
              <Field label="Ville"><input type="text" value={ent.ville} onChange={e => setEnt(v => ({ ...v, ville: e.target.value }))} style={inputCls} /></Field>
              <Field label="Téléphone"><input type="tel" inputMode="numeric" value={ent.tel} onChange={e => setEnt(v => ({ ...v, tel: formaterTelephone(e.target.value) }))} style={inputCls} placeholder="XX XX XX XX XX" /></Field>
            </div>

            <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '0.5rem', paddingTop: '0.75rem' }}>
              <p style={{ fontSize: '0.82rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>Représentant (pour la convention)</p>
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: '0 1rem' }}>
                <Field label="Civilité"><CiviliteSelect value={rep.civilite} onChange={v => setRep(r => ({ ...r, civilite: v }))} /></Field>
                <Field label="Nom"><input type="text" value={rep.nom} onChange={e => setRep(r => ({ ...r, nom: formaterNomFamille(e.target.value) }))} style={inputCls} /></Field>
                <Field label="Prénom"><input type="text" value={rep.prenom} onChange={e => setRep(r => ({ ...r, prenom: formaterPrenom(e.target.value) }))} style={inputCls} /></Field>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '0.5rem', paddingTop: '0.75rem' }}>
              <p style={{ fontSize: '0.82rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>Personne chargée de l'inscription</p>
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: '0 1rem' }}>
                <Field label="Civilité"><CiviliteSelect value={contact.civilite} onChange={v => setContact(c => ({ ...c, civilite: v }))} /></Field>
                <Field label="Nom"><input type="text" value={contact.nom} onChange={e => setContact(c => ({ ...c, nom: formaterNomFamille(e.target.value) }))} style={inputCls} /></Field>
                <Field label="Prénom"><input type="text" value={contact.prenom} onChange={e => setContact(c => ({ ...c, prenom: formaterPrenom(e.target.value) }))} style={inputCls} /></Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                <Field label="Téléphone"><input type="tel" inputMode="numeric" value={contact.tel} onChange={e => setContact(c => ({ ...c, tel: formaterTelephone(e.target.value) }))} style={inputCls} placeholder="XX XX XX XX XX" /></Field>
                <Field label="Email"><input type="email" value={contact.mail} onChange={e => setContact(c => ({ ...c, mail: e.target.value }))} style={inputCls} /></Field>
              </div>
            </div>
          </Section>
        )}

        {/* Section 3 : Particulier */}
        {inscriptionType === 'particulier' && (
          <Section title="INSCRIPTION À TITRE PARTICULIER">
            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: '0 1rem' }}>
              <Field label="Civilité"><CiviliteSelect value={part.civilite} onChange={v => setPart(p => ({ ...p, civilite: v }))} /></Field>
              <Field label="Nom" required><input type="text" value={part.nom} onChange={e => setPart(p => ({ ...p, nom: formaterNomFamille(e.target.value) }))} style={{ ...inputCls, borderColor: submitAttempted && !part.nom ? '#ef4444' : inputCls.border }} /></Field>
              <Field label="Prénom"><input type="text" value={part.prenom} onChange={e => setPart(p => ({ ...p, prenom: formaterPrenom(e.target.value) }))} style={inputCls} /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
              <Field label="Téléphone"><input type="tel" inputMode="numeric" value={part.tel} onChange={e => setPart(p => ({ ...p, tel: formaterTelephone(e.target.value) }))} style={inputCls} placeholder="XX XX XX XX XX" /></Field>
              <Field label="Email"><input type="email" value={part.mail} onChange={e => setPart(p => ({ ...p, mail: e.target.value }))} style={inputCls} /></Field>
            </div>
          </Section>
        )}

        {/* Section 4 : Facturation */}
        <Section title="FACTURATION">
          <p style={{ fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic', marginBottom: '0.5rem' }}>
            Si vous demandez une prise en charge par un financeur, l'accord doit nous parvenir <u>avant le début de la formation</u>.
          </p>
          <Field label="Stagiaire, entreprise ou organisme à facturer">
            <input type="text" value={fact.destinataire} onChange={e => setFact(f => ({ ...f, destinataire: e.target.value }))} style={inputCls} />
          </Field>
          <Field label="Adresse (si différente)">
            <input type="text" value={fact.adresse} onChange={e => setFact(f => ({ ...f, adresse: e.target.value }))} style={inputCls} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0 1rem' }}>
            <Field label="Code postal"><input type="text" value={fact.cp} onChange={e => setFact(f => ({ ...f, cp: e.target.value }))} style={inputCls} /></Field>
            <Field label="Ville"><input type="text" value={fact.ville} onChange={e => setFact(f => ({ ...f, ville: e.target.value }))} style={inputCls} /></Field>
          </div>
        </Section>

        {/* Section 5 : Activités */}
        <Section title="ACTIVITÉ(S) DE L'ENTREPRISE">
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelCls}>Ajouter une activité</label>
            <select
              value=""
              onChange={e => {
                const val = e.target.value;
                if (val && !activitesChoisies.includes(val))
                  setActivitesChoisies(prev => [...prev, val]);
              }}
              style={selectCls}
            >
              <option value="">— Sélectionner une activité —</option>
              {activitesList.filter(a => !activitesChoisies.includes(a)).map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          {activitesChoisies.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
              {activitesChoisies.map(a => (
                <span key={a} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.25rem 0.65rem', background: '#fff7ed', border: '1px solid #E87722', borderRadius: 20, fontSize: '0.82rem', color: '#c2410c', fontWeight: 600 }}>
                  {a}
                  <button
                    type="button"
                    onClick={() => setActivitesChoisies(prev => prev.filter(x => x !== a))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '0 1px', fontSize: '0.8rem', lineHeight: 1, fontWeight: 700 }}
                  >✕</button>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
            <Field label="Autre activité à préciser">
              <input type="text" value={activiteAutre} onChange={e => setActiviteAutre(e.target.value)} style={inputCls} placeholder="Précisez..." />
            </Field>
            <Field label="Autre activité (2ème)">
              <input type="text" value={autreActivite} onChange={e => setAutreActivite(e.target.value)} style={inputCls} placeholder="Précisez..." />
            </Field>
          </div>
        </Section>

        {/* Section 6 : Stagiaires */}
        <Section title="STAGIAIRE(S)">
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem', fontStyle: 'italic' }}>
            Pour répondre à l'obligation du Passeport prévention en santé au travail, instauré par la loi n° 2021-1018 du 2 août 2021, veuillez renseigner les informations ci-dessous.
          </p>

          {stagiaires.map((s, idx) => (
            <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem', marginBottom: '0.75rem', background: '#fafafa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 700, color: '#E87722', fontSize: '0.9rem' }}>Stagiaire {idx + 1}</span>
                {stagiaires.length > 1 && (
                  <button onClick={() => removeStagiaire(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.85rem', padding: '0.2rem 0.4rem' }}>
                    ✕ Supprimer
                  </button>
                )}
              </div>
              {/* Sélection depuis la liste existante */}
              {stagiairesExistants.length > 0 && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={labelCls}>Sélectionner un salarié déjà enregistré (pré-remplissage)</label>
                  <select
                    style={{ ...selectCls, borderColor: '#E87722' }}
                    value=""
                    onChange={e => {
                      if (!e.target.value) return;
                      const found = stagiairesExistants.find(st => st.stagiaire_id === e.target.value);
                      if (!found) return;
                      const fmtDate = found.date_naissance ? found.date_naissance.slice(0, 10) : '';
                      setStagiaires(prev => prev.map((row, i) => i === idx ? {
                        ...row,
                        civilite: found.civilite || '',
                        nom_usage: formaterNomFamille(found.nom || ''),
                        nom_naissance: formaterNomFamille(found.nom_jeune_fille || ''),
                        prenom: formaterPrenom(found.prenoms || ''),
                        date_naissance: fmtDate,
                        nss: found.num_securite_sociale || '',
                        tel: formaterTelephone(found.tel || ''),
                      } : row));
                    }}
                  >
                    <option value="">— Choisir un salarié existant —</option>
                    {stagiairesExistants.map(st => {
                      const dob = st.date_naissance ? new Date(st.date_naissance).toLocaleDateString('fr-FR') : '';
                      return (
                        <option key={st.stagiaire_id} value={st.stagiaire_id}>
                          {st.civilite ? st.civilite + ' ' : ''}{st.prenoms} {st.nom}{dob ? ` (${dob})` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 1fr', gap: '0 0.75rem' }}>
                <Field label="Civilité" required>
                  <CiviliteSelect value={s.civilite} onChange={v => updateStagiaire(idx, 'civilite', v)} style={submitAttempted && !s.civilite ? { borderColor: '#ef4444' } : {}} />
                </Field>
                <Field label="Nom d'usage" required>
                  <input type="text" value={s.nom_usage} onChange={e => updateStagiaire(idx, 'nom_usage', formaterNomFamille(e.target.value))} style={{ ...inputCls, borderColor: submitAttempted && !s.nom_usage ? '#ef4444' : inputCls.border }} />
                </Field>
                <Field label="Nom de naissance" required>
                  <input type="text" value={s.nom_naissance} onChange={e => updateStagiaire(idx, 'nom_naissance', formaterNomFamille(e.target.value))} style={{ ...inputCls, borderColor: submitAttempted && !s.nom_naissance ? '#ef4444' : inputCls.border }} />
                </Field>
                <Field label="Prénom" required>
                  <input type="text" value={s.prenom} onChange={e => updateStagiaire(idx, 'prenom', formaterPrenom(e.target.value))} style={{ ...inputCls, borderColor: submitAttempted && !s.prenom ? '#ef4444' : inputCls.border }} />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
                <Field label="Date de naissance" required>
                  <input type="date" value={s.date_naissance} onChange={e => updateStagiaire(idx, 'date_naissance', e.target.value)} style={{ ...inputCls, borderColor: submitAttempted && !s.date_naissance ? '#ef4444' : inputCls.border }} />
                </Field>
                <Field label="N° Sécurité sociale (13 chiffres)" required>
                  <input type="text" value={s.nss} onChange={e => updateStagiaire(idx, 'nss', e.target.value)} maxLength={13} style={{ ...inputCls, borderColor: submitAttempted && !s.nss ? '#ef4444' : inputCls.border }} />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
                <Field label="Téléphone portable">
                  <input type="tel" inputMode="numeric" value={s.tel} onChange={e => updateStagiaire(idx, 'tel', formaterTelephone(e.target.value))} style={inputCls} placeholder="XX XX XX XX XX" />
                </Field>
                <Field label="Poste occupé">
                  <input type="text" value={s.poste} onChange={e => updateStagiaire(idx, 'poste', e.target.value)} style={inputCls} />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
                <div>
                  <Field label="Personne en situation de handicap" required>
                    <select
                      value={s.handicap}
                      onChange={e => {
                        updateStagiaire(idx, 'handicap', e.target.value);
                        if (e.target.value !== 'Oui') updateStagiaire(idx, 'handicap_detail', '');
                      }}
                      style={{ ...selectCls, borderColor: submitAttempted && !s.handicap ? '#ef4444' : selectCls.border }}
                    >
                      <option value="">—</option>
                      <option value="Oui">Oui</option>
                      <option value="Non">Non</option>
                    </select>
                  </Field>
                  {s.handicap === 'Oui' && (
                    <Field label="Précisez le handicap" required>
                      <input
                        type="text"
                        value={s.handicap_detail}
                        onChange={e => updateStagiaire(idx, 'handicap_detail', e.target.value)}
                        style={{ ...inputCls, borderColor: submitAttempted && !s.handicap_detail?.trim() ? '#ef4444' : inputCls.border }}
                        placeholder="Nature du handicap..."
                      />
                    </Field>
                  )}
                </div>
                <div>
                  <Field label="Difficultés à l'oral, de lecture, d'écriture, autre…" required>
                    <select
                      value={s.difficultes}
                      onChange={e => {
                        updateStagiaire(idx, 'difficultes', e.target.value);
                        if (e.target.value !== 'Oui') updateStagiaire(idx, 'difficultes_oui', '');
                      }}
                      style={{ ...selectCls, borderColor: submitAttempted && !s.difficultes ? '#ef4444' : selectCls.borderColor }}
                    >
                      <option value="">—</option>
                      <option value="Oui">Oui</option>
                      <option value="Non">Non</option>
                    </select>
                  </Field>
                  {s.difficultes === 'Oui' && (
                    <Field label="Précisez les difficultés" required>
                      <input
                        type="text"
                        value={s.difficultes_oui}
                        onChange={e => updateStagiaire(idx, 'difficultes_oui', e.target.value)}
                        style={{ ...inputCls, borderColor: submitAttempted && !s.difficultes_oui?.trim() ? '#ef4444' : inputCls.border }}
                        placeholder="Nature des difficultés..."
                      />
                    </Field>
                  )}
                </div>
              </div>
            </div>
          ))}

          {maxStagiaires !== null && (
            <p style={{ fontSize: '0.8rem', color: stagiaires.length >= maxStagiaires ? '#dc2626' : '#6b7280', marginBottom: '0.5rem', textAlign: 'center' }}>
              {stagiaires.length >= maxStagiaires
                ? `⚠️ Limite atteinte — ${maxStagiaires} stagiaire${maxStagiaires > 1 ? 's' : ''} maximum selon ce devis`
                : `${stagiaires.length} / ${maxStagiaires} stagiaire${maxStagiaires > 1 ? 's' : ''}`}
            </p>
          )}
          <button
            onClick={addStagiaire}
            disabled={maxStagiaires !== null && stagiaires.length >= maxStagiaires}
            style={{
              padding: '0.5rem 1rem', borderRadius: 8, width: '100%',
              fontWeight: 600, fontSize: '0.88rem',
              border: maxStagiaires !== null && stagiaires.length >= maxStagiaires ? '1.5px dashed #d1d5db' : '1.5px dashed #E87722',
              background: maxStagiaires !== null && stagiaires.length >= maxStagiaires ? '#f9fafb' : '#fff7ed',
              color: maxStagiaires !== null && stagiaires.length >= maxStagiaires ? '#9ca3af' : '#E87722',
              cursor: maxStagiaires !== null && stagiaires.length >= maxStagiaires ? 'not-allowed' : 'pointer',
            }}>
            + Ajouter un stagiaire
          </button>
        </Section>

        {/* Choix des sessions — avant la signature */}
        {sessionsChoix.length > 0 && (
          <Section title="CHOIX DES SESSIONS" color="#E87722">
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
              Chaque stagiaire est inscrit à <strong>une seule session</strong>. Vous pouvez choisir la même session pour tous les stagiaires, ou une session différente par stagiaire.
              Si aucune date ne vous convient, choisissez « Aucune de ces dates ne me convient » : votre fiche est enregistrée et nous vous recontacterons pour vous proposer une nouvelle date.
            </p>

            {/* Session unique pour tous */}
            <div style={{ marginBottom: '1rem', padding: '0.85rem', background: '#eff6ff', borderRadius: 8, border: '1px solid #93c5fd' }}>
              <label style={{ ...labelCls, color: '#1e40af', marginBottom: '0.4rem' }}>
                Même session pour tous les stagiaires
              </label>
              <select
                value={sessionPourTous}
                onChange={e => { setSessionPourTous(e.target.value); setSessionsParStagiaire({}); }}
                style={selectCls}
              >
                <option value="">— Choisir une session pour tous —</option>
                {sessionsChoix.map(sess => {
                  const debut = sess.date_debut ? new Date(sess.date_debut).toLocaleDateString('fr-FR') : '?';
                  const fin   = sess.date_fin   ? new Date(sess.date_fin).toLocaleDateString('fr-FR')   : '?';
                  const places = `${sess.nb_inscrits || 0}/${sess.stagiaires_max || '?'}`;
                  return (
                    <option key={sess.session_id} value={sess.session_id}>
                      {sess.session_id} — Du {debut} au {fin} — {places} inscrits{sess.lieu ? ` — ${sess.lieu}` : ''}
                    </option>
                  );
                })}
                <option value={SESSION_AUCUNE}>Aucune de ces dates ne me convient</option>
              </select>
              {sessionPourTous && (
                <button onClick={() => setSessionPourTous('')}
                  style={{ marginTop: '0.5rem', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline', padding: 0 }}>
                  ✕ Effacer et choisir par stagiaire
                </button>
              )}
            </div>

            {/* Session par stagiaire (si pas de sélection globale) */}
            {!sessionPourTous && (
              <div>
                <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.6rem', fontStyle: 'italic' }}>
                  Ou choisissez une session différente pour chaque stagiaire :
                </p>
                {stagiaires.filter(s => s.nom_usage || s.nom_naissance || s.prenom).map((s, idx) => {
                  const nom = [s.prenom, s.nom_usage || s.nom_naissance].filter(Boolean).join(' ') || `Stagiaire ${idx + 1}`;
                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ flex: '0 0 180px', fontSize: '0.88rem', fontWeight: 600, color: '#374151', minWidth: 120 }}>
                        {nom}
                      </span>
                      <select
                        value={sessionsParStagiaire[idx] || ''}
                        onChange={e => setSessionsParStagiaire(prev => ({ ...prev, [idx]: e.target.value }))}
                        style={{ ...selectCls, flex: 1, minWidth: 200 }}
                      >
                        <option value="">— Session —</option>
                        {sessionsChoix.map(sess => {
                          const debut = sess.date_debut ? new Date(sess.date_debut).toLocaleDateString('fr-FR') : '?';
                          const fin   = sess.date_fin   ? new Date(sess.date_fin).toLocaleDateString('fr-FR')   : '?';
                          const places = `${sess.nb_inscrits || 0}/${sess.stagiaires_max || '?'}`;
                          return (
                            <option key={sess.session_id} value={sess.session_id}>
                              {sess.session_id} — Du {debut} au {fin} — {places} inscrits
                            </option>
                          );
                        })}
                        <option value={SESSION_AUCUNE}>Aucune de ces dates ne me convient</option>
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
            {aucuneChoisie && (
              <div style={{ marginTop: '1rem', padding: '0.85rem', background: '#fff7ed', borderRadius: 8, border: '1px solid #fdba74' }}>
                <label style={{ ...labelCls, color: '#9a3412' }}>
                  Précisions sur vos disponibilités (facultatif)
                </label>
                <textarea
                  value={datesSouhaitees}
                  onChange={e => setDatesSouhaitees(e.target.value.slice(0, 500))}
                  rows={3}
                  placeholder="Ex. : plutôt en janvier, éviter les vacances scolaires…"
                  style={{ ...inputCls, resize: 'vertical', fontFamily: 'inherit' }}
                />
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: '#9a3412' }}>
                  Les stagiaires concernés ne seront inscrits à aucune session pour le moment : nous reviendrons vers vous avec une nouvelle date.
                </p>
              </div>
            )}
            {(contactCej.email || contactCej.tel) && (
              <p style={{ marginTop: '0.75rem', fontSize: '0.82rem', color: '#6b7280' }}>
                Une question sur les dates ?{' '}
                {contactCej.email && (
                  <a href={`mailto:${contactCej.email}?subject=${encodeURIComponent(`Dates de formation — ${devis?.intitule_formation || ''} — devis ${devis?.devis_id || ''}`)}`}
                    style={{ color: '#E87722', fontWeight: 600 }}>
                    Nous écrire
                  </a>
                )}
                {contactCej.email && contactCej.tel && ' · '}
                {contactCej.tel && <span>ou nous appeler au <strong>{contactCej.tel}</strong></span>}
              </p>
            )}
          </Section>
        )}

        {/* Signature */}
        <Section title="SIGNATURE ET ENGAGEMENT">
          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem', fontStyle: 'italic' }}>
            Je déclare avoir pris connaissance des conditions d'inscription figurant sur le programme de formation.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '0 1rem' }}>
            <Field label="Fait à" required>
              <input type="text" value={faitA} onChange={e => setFaitA(e.target.value)} style={{ ...inputCls, borderColor: submitAttempted && !faitA ? '#ef4444' : inputCls.border }} placeholder="Ville..." />
            </Field>
            <Field label="Le">
              <input type="date" value={faitLe} onChange={e => setFaitLe(e.target.value)} style={inputCls} />
            </Field>
          </div>
        </Section>

        {/* Bouton envoi */}
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          {(!blocage?.isBlocked || isAdminMode) && (
            <button
              onClick={handleSubmit}
              disabled={saving}
              style={{
                padding: '0.85rem 2.5rem', borderRadius: 10, border: 'none',
                background: saving ? '#9ca3af' : (isAdminMode && blocage?.isBlocked ? '#7c3aed' : '#E87722'), color: '#fff',
                fontWeight: 700, fontSize: '1.05rem', cursor: saving ? 'not-allowed' : 'pointer',
                boxShadow: `0 4px 12px ${isAdminMode && blocage?.isBlocked ? 'rgba(124,58,237,0.3)' : 'rgba(232,119,34,0.3)'}`,
              }}>
              {saving ? '⏳ Envoi en cours...' : (isAdminMode && blocage?.isBlocked ? '🔑 Enregistrer (admin)' : '📤 Envoyer ma fiche d\'inscription')}
            </button>
          )}
          {ficheExistante && !blocage?.isBlocked && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#9ca3af' }}>
              ℹ️ Une soumission précédente existait — elle sera mise à jour.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
